import { Component } from '../../../components/Component.js';
import { storage } from '../../../js/utils/storage.js';
import { applyTranslations, getCurrentLanguage, loadTranslations } from '../../../js/translations.js';
import { hydratePricingFromPlans, isTrialOfferEligible } from '../../../js/utils/pricingPlans.js';
import { handlePaidTrialCheckoutClick } from '../../../js/utils/quotaErrorHandler.js';
import { SFW_ORIGIN, isSfwSite } from '../../../js/config.js';
import { startCryptoCheckout } from '../../../js/utils/cryptoCheckout.js';
import { bindCryptoPricing, cryptoPackTitle, loadCryptoSpendEstimate, renderCryptoCardNote, renderCryptoPricing } from '../../../js/utils/cryptoPricing.js';

const renderFeatureList = (tierKey, { voiceEnabled, groupConversations = false, shortContext = false }) => `
          <ul>
            <li class="yes" data-translate="pricing.${tierKey}.features.1" data-pricing-feature="pricing.${tierKey}.features.1">~messages</li>
            <li class="${groupConversations ? 'yes' : 'no'}" data-translate="pricing.${tierKey}.features.2">Group conversations</li>
            <li class="${voiceEnabled ? 'yes' : 'no'}" data-translate="pricing.${tierKey}.features.3" data-pricing-feature="pricing.${tierKey}.features.3">Voice</li>
            <li class="${shortContext ? 'no' : 'yes'}" data-translate="pricing.${tierKey}.features.7" data-pricing-feature="pricing.${tierKey}.features.7">Context tokens</li>
            <li class="yes" data-translate="pricing.${tierKey}.features.8">${tierKey === 'premium' || tierKey === 'creator' ? 'Max models included' : 'Plus models included'}</li>
            <li class="yes" data-translate="pricing.${tierKey}.features.0">All outfits</li>
            <li class="yes" data-translate="pricing.${tierKey}.features.4" data-pricing-feature="pricing.${tierKey}.features.4">Character creations</li>
            <li class="yes" data-translate="pricing.${tierKey}.features.5" data-pricing-feature="pricing.${tierKey}.features.5">Scenario creations</li>
            <li class="yes" data-translate="pricing.${tierKey}.features.6" data-pricing-feature="pricing.${tierKey}.features.6">Sprite credits</li>
          </ul>`;

class PricingComponent extends Component {
  static instance = null;

  static getInstance(element, options = {}) {
    if (!PricingComponent.instance) {
      PricingComponent.instance = new PricingComponent(element, options);
    } else if (element && element !== PricingComponent.instance.element) {
      PricingComponent.instance.element = element;
      PricingComponent.instance.init();
    }
    return PricingComponent.instance;
  }

  constructor(element, options = {}) {
    if (PricingComponent.instance) return PricingComponent.instance;
    super(element, options);
    this.handleLanguageChanged = (e) => e.detail?.language && this.render();
    this.handleUserDataStorage = (e) => e.key === 'userData' && this.render();
    this.handleAuthChanged = () => this.render();
    this.init();
    PricingComponent.instance = this;
  }

  init() {
    document.addEventListener('languageChanged', this.handleLanguageChanged);
    window.addEventListener('storage', this.handleUserDataStorage);
    document.addEventListener('auth:login:success', this.handleAuthChanged);
    document.addEventListener('auth:logout', this.handleAuthChanged);
    this.render();
  }

  async render() {
    const stripeConfig = window.STRIPE_CONFIG || { PAYMENT_LINKS: {} };

    let pricingTranslations = {};
    let currentLang = 'en';
    try {
      currentLang = getCurrentLanguage() || 'en';
      await loadTranslations('pricing', currentLang);
      const translations = window.translations || {};
      const langTranslations = translations[currentLang] || translations.en || {};
      pricingTranslations = langTranslations.pricing || {};
    } catch (error) {
      console.error('Error loading pricing translations:', error);
    }

    const showTrialPlan = isTrialOfferEligible();
    const starterButtonText = this.getButtonText('starter', pricingTranslations);
    const premiumButtonText = this.getButtonText('single', pricingTranslations);
    const creatorButtonText = this.getButtonText('harem', pricingTranslations);
    const trialButtonText = pricingTranslations.pricing?.trial?.cta || 'Get started';

    if (!isSfwSite()) {
      const spend = await loadCryptoSpendEstimate();
      this.element.innerHTML = `
        <h1 style="font-size: 2.25rem;margin:20px;">${cryptoPackTitle(currentLang)}</h1>
        ${renderCryptoPricing(currentLang, spend)}
        ${renderCryptoCardNote(currentLang, `${SFW_ORIGIN}/pricing.html`)}`;
      bindCryptoPricing(this.element, (button) => this.handleCryptoCheckout(button));
      try {
        applyTranslations('pricing', currentLang);
      } catch (error) {
        console.error('Translation error:', error);
      }
      return;
    }

    const trialPlanHtml = showTrialPlan ? `
        <div class="pricing-plan" data-pricing-plan="trial">
          <h2 data-translate="pricing.trial.title">Trial Offer</h2>
          <p class="price">
            <span class="price-amount"><span data-translate="pricing.trial.period">48h</span></span>
          </p>
          ${renderFeatureList('trial', { voiceEnabled: true, shortContext: true })}
          <button class="plan-button trial-offer-button" data-plan="trial" type="button">${trialButtonText}</button>
          <p class="trial-disclaimer">
            <small data-translate="pricing.quota_exhausted.disclaimer" data-pricing-feature="pricing.quota_exhausted.disclaimer" data-pricing-plan-tier="trial">$0.99 for 48 hours, then $8.50/month. Cancel anytime</small>
          </p>
        </div>` : '';

    this.element.innerHTML = `
      <h1 data-translate="pricing.title" style="font-size: 2.25rem;margin:20px;">Pricing Plans</h1>
      <div class="pricing-toggle">
        <button class="monthly-btn active" data-translate="pricing.monthly">Monthly</button>
        <button class="yearly-btn">
          <span data-translate="pricing.yearly">Yearly</span>:
          <span class="discount-text" data-translate="pricing.yearlyDiscount">get 2 months for free</span>
        </button>
      </div>
      <div id="pricing-container"${showTrialPlan ? ' class="has-trial-plan"' : ''}>
        ${trialPlanHtml}
        <div class="pricing-plan" data-pricing-plan="starter">
          <h2 data-translate="pricing.starter.title">Starter Tier</h2>
          <p class="price">
            <span class="price-amount"><span data-translate="pricing.monthly">monthly</span></span>
            <span class="yearly-price" style="display: none;"><span data-translate="pricing.yearly">yearly</span></span>
          </p>
          ${renderFeatureList('starter', { voiceEnabled: false, shortContext: true })}
          <button class="plan-button subscribe-button subscribe-btn" data-plan="starter"
                  data-monthly-link="${stripeConfig.PAYMENT_LINKS.starter_monthly || ''}"
                  data-yearly-link="${stripeConfig.PAYMENT_LINKS.starter_yearly || ''}">${starterButtonText}</button>
        </div>

        <div class="pricing-plan featured" data-pricing-plan="single">
          <div class="featured-badge" data-translate="pricing.popular" style="display: none;">Premium Tier</div>
          <h2 data-translate="pricing.premium.title">Premium Tier</h2>
          <p class="price">
            <span class="price-amount"><span data-translate="pricing.monthly">monthly</span></span>
            <span class="yearly-price" style="display: none;"><span data-translate="pricing.yearly">yearly</span></span>
          </p>
          ${renderFeatureList('premium', { voiceEnabled: true, groupConversations: true })}
          <button class="plan-button subscribe-button subscribe-btn" data-plan="single"
                  data-monthly-link="${stripeConfig.PAYMENT_LINKS.single_monthly || ''}"
                  data-yearly-link="${stripeConfig.PAYMENT_LINKS.single_yearly || ''}">${premiumButtonText}</button>
        </div>

        <div class="pricing-plan" data-pricing-plan="harem">
          <h2 data-translate="pricing.creator.title">Creator Tier</h2>
          <p class="price">
            <span class="price-amount"><span data-translate="pricing.monthly">monthly</span></span>
            <span class="yearly-price" style="display: none;"><span data-translate="pricing.yearly">yearly</span></span>
          </p>
          ${renderFeatureList('creator', { voiceEnabled: true, groupConversations: true })}
          <button class="plan-button subscribe-button subscribe-btn" data-plan="harem"
                  data-monthly-link="${stripeConfig.PAYMENT_LINKS.harem_monthly || ''}"
                  data-yearly-link="${stripeConfig.PAYMENT_LINKS.harem_yearly || ''}">${creatorButtonText}</button>
        </div>
      </div>
      <span
        class="pricing-messages-disclaimer"
        data-translate="pricing.messages_estimate_disclaimer"
      >
        Estimates are based on typical usage (median) and are updated weekly. You can use way less (or way more) based on message length and model. Audio counts assume xAI voices.
      </span>
      <span
        class="pricing-messages-disclaimer"
        data-translate="pricing.voice_byok_footnote"
      >
        * You can bring your own OpenRouter API key to get voice messages regardless of your membership tier. See <a href="/guide/byok#voices">our guide</a>
      </span>
    `;

    this.monthlyButton = this.element.querySelector('.monthly-btn');
    this.yearlyButton = this.element.querySelector('.yearly-btn');
    this.subscribeButtons = this.element.querySelectorAll('.subscribe-button');
    this.trialButton = this.element.querySelector('[data-pricing-plan="trial"] .trial-offer-button');

    try {
      applyTranslations('pricing', currentLang);
      await hydratePricingFromPlans(this.element, currentLang);
    } catch (error) {
      console.error('Translation error:', error);
    }

    this.markStarterVoiceByokNote();

    this.addEventListeners();
  }

  markStarterVoiceByokNote() {
    const voiceItem = this.element.querySelector('[data-pricing-plan="starter"] [data-pricing-feature="pricing.starter.features.3"]');
    if (!voiceItem || voiceItem.querySelector('.voice-byok-asterisk')) return;
    const mark = document.createElement('sup');
    mark.className = 'voice-byok-asterisk';
    mark.textContent = '*';
    mark.setAttribute('aria-hidden', 'true');
    voiceItem.appendChild(mark);
  }

  addEventListeners() {
    this.monthlyButton?.addEventListener('click', () => this.toggleBilling(false));
    this.yearlyButton?.addEventListener('click', () => this.toggleBilling(true));
    this.subscribeButtons?.forEach((btn) =>
      btn.addEventListener('click', (e) => this.handleSubscribe(btn, e)));
    this.trialButton?.addEventListener('click', (e) => this.handleTrialSubscribe(this.trialButton, e));
  }

  toggleBilling(isYearly) {
    this.monthlyButton?.classList.toggle('active', !isYearly);
    this.yearlyButton?.classList.toggle('active', isYearly);

    this.element.querySelectorAll('[data-pricing-plan]:not([data-pricing-plan="trial"]) .price-amount').forEach((el) => {
      el.style.display = isYearly ? 'none' : 'inline-block';
    });
    this.element.querySelectorAll('[data-pricing-plan]:not([data-pricing-plan="trial"]) .yearly-price').forEach((el) => {
      el.style.display = isYearly ? 'inline-block' : 'none';
    });
  }

  async handleTrialSubscribe(button, event) {
    event.preventDefault();
    if (!this.isUserAuthenticated()) {
      this.showLoginRequired();
      return;
    }
    await handlePaidTrialCheckoutClick(button, 'Please log in to start your trial');
  }

  async handleCryptoCheckout(button) {
    if (!this.isUserAuthenticated()) {
      this.showLoginRequired();
      return;
    }
    const plan = button.getAttribute('data-crypto-plan');
    try {
      await startCryptoCheckout({ plan, button });
    } catch (error) {
      button.disabled = false;
      import('../../../js/utils/UnifiedNotificationSystem.js').then(({ showError }) => {
        showError(error.message || 'Could not start crypto checkout', { icon: 'credit-card', duration: 5000 });
      });
    }
  }

  async handleSubscribe(button, event) {
    event.preventDefault();

    if (!this.isUserAuthenticated()) {
      this.showLoginRequired();
      return;
    }

    const userEmail = this.getUserData()?.email;
    if (!userEmail) {
      import('../../../js/utils/UnifiedNotificationSystem.js').then(({ showError }) => {
        showError('User email not found. Please log in again.', { icon: 'user-times', duration: 5000 });
      });
      return;
    }

    let userId = null;
    try {
      const token = await window.tokenManager?.getValidAccessToken?.() ?? null;
      if (token) {
        userId = JSON.parse(atob(token.split('.')[1])).sub;
      }
    } catch (error) {
      console.warn('Could not extract user_id from JWT:', error);
    }

    if (!userId) {
      console.warn('Could not extract user_id from JWT; aborting subscription');
      import('../../../js/utils/UnifiedNotificationSystem.js').then(({ showWarning }) => {
        showWarning('Please reload the page and log in again.', { icon: 'exclamation-triangle', duration: 5000 });
      });
      return;
    }

    const isYearly = this.yearlyButton?.classList.contains('active');
    const baseLink = button.getAttribute(isYearly ? 'data-yearly-link' : 'data-monthly-link');

    if (!baseLink) {
      import('../../../js/utils/UnifiedNotificationSystem.js').then(({ showError }) => {
        showError('Payment link not configured', { icon: 'credit-card', duration: 5000 });
      });
      return;
    }

    const url = new URL(baseLink);
    url.searchParams.set('prefilled_email', userEmail);
    url.searchParams.set('client_reference_id', userId);

    button.textContent = 'Redirecting...';
    button.disabled = true;
    window.location.href = url.toString();
  }

  isUserAuthenticated() {
    return window.tokenManager?.isAuthenticated?.() === true && storage.get('userData')?.isLoggedIn === true;
  }

  getUserData() {
    return storage.get('userData') || null;
  }

  getCurrentUserTier() {
    const userData = this.getUserData();
    return userData?.current_tier || 'free';
  }

  getButtonText(planDataAttribute, pricingTranslations) {
    const currentTier = this.getCurrentUserTier();
    const hasPaidTier = ['starter', 'single', 'harem_protagonist'].includes(currentTier);

    const tierMapping = {
      starter: 'starter',
      single: 'single',
      harem: 'harem_protagonist',
    };

    const planTier = tierMapping[planDataAttribute];
    const getByPath = (obj, key) => key.split('.').reduce((acc, part) => acc?.[part], obj);

    if (planTier === currentTier) {
      return getByPath(pricingTranslations, 'pricing.current') || 'Current';
    }
    if (hasPaidTier) {
      return getByPath(pricingTranslations, 'pricing.update') || 'Update';
    }
    return getByPath(pricingTranslations, 'pricing.subscribe') || 'Subscribe';
  }

  async showLoginRequired() {
    try {
      if (window.loginModalController?.show) {
        await window.loginModalController.show(false);
        return;
      }
      const { default: loginModalController } = await import('../../../js/components/LoginModalController.js');
      await loginModalController?.show?.(false);
    } catch (error) {
      console.error('Failed to show login modal:', error);
    }
  }

  destroy() {
    document.removeEventListener('languageChanged', this.handleLanguageChanged);
    window.removeEventListener('storage', this.handleUserDataStorage);
    document.removeEventListener('auth:login:success', this.handleAuthChanged);
    document.removeEventListener('auth:logout', this.handleAuthChanged);
    super.destroy();
  }
}

export { PricingComponent };
export default PricingComponent.getInstance;
